/* ============================================================================
   More n Mine — flying magpie
   ----------------------------------------------------------------------------
   The mascot periodically flies across the viewport on a curved path and exits
   off-screen, then waits and comes back on a different route.

   Structure (built once, on demand):

     .magpie-sky      fixed full-viewport overlay, pointer-events:none
       .magpie        positioned each frame by JS (translate + rotate + flip)
         .magpie-strip  CSS steps() wing-flap over the sprite sheet

   Three nested elements because three different things own a transform: the
   flight path, the wing cycle, and the horizontal flip. Sharing one element
   would mean JS and CSS overwriting each other every frame.

   Config comes from window.MnMMagpieConfig (written by theme.liquid from the
   theme settings).
   ========================================================================== */
(function () {
  'use strict';

  var C = window.MnMMagpieConfig || {};
  var cfg = {
    enabled:   C.enabled !== false,
    frames:    C.frames   || 12,
    size:      C.size     || 130,   // css px, wing-tip to wing-tip
    flapMs:    C.flapMs   || 620,   // one full wing cycle
    crossMs:   C.crossMs  || 7000,  // time to cross the viewport
    firstMs:   C.firstMs  || 4000,  // delay before the first flight
    minGapMs:  C.minGapMs || 18000, // idle time between flights
    maxGapMs:  C.maxGapMs || 38000,
    sprite:    C.sprite   || ''
  };

  var reduced = false;
  try {
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  // A bird flapping across the screen is the definition of non-essential
  // motion, so it is the first thing to go on reduced-motion or a weak device.
  if (!cfg.enabled || !cfg.sprite || reduced || window.ARC_LOWPOWER === true) return;

  var sky, bird, strip, flying = false, timer = null;

  function build() {
    sky = document.createElement('div');
    sky.className = 'magpie-sky';
    sky.setAttribute('aria-hidden', 'true');

    bird = document.createElement('div');
    bird.className = 'magpie';
    bird.style.width = wingspan() + 'px';

    strip = document.createElement('div');
    strip.className = 'magpie-strip';
    strip.style.width = (cfg.frames * 100) + '%';
    strip.style.backgroundImage = 'url("' + cfg.sprite + '")';
    strip.style.animationDuration = cfg.flapMs + 'ms';
    strip.style.animationTimingFunction = 'steps(' + cfg.frames + ')';

    bird.appendChild(strip);
    sky.appendChild(bird);
    document.body.appendChild(sky);
  }

  function rand(a, b) { return a + Math.random() * (b - a); }

  /* The configured wingspan is a desktop figure. On a phone it would be a third
     of the screen, so cap it at a share of the viewport width. */
  function wingspan() {
    return Math.round(Math.min(cfg.size, window.innerWidth * 0.26));
  }

  /* Pick a route across the viewport. Every route starts and ends fully
     off-screen, so the bird is never seen popping into or out of existence —
     it always enters and leaves past an edge. */
  function route() {
    var vw = window.innerWidth, vh = window.innerHeight;
    var pad = wingspan() * 1.6;           // clearance beyond the edge
    var toLeft = Math.random() < 0.5;     // travel direction

    var kind = Math.random();
    var y0, y1;
    if (kind < 0.45) {            // level crossing, high or low
      y0 = rand(vh * 0.10, vh * 0.75);
      y1 = y0 + rand(-vh * 0.12, vh * 0.12);
    } else if (kind < 0.8) {      // descending or climbing diagonal
      y0 = rand(vh * 0.05, vh * 0.3);
      y1 = rand(vh * 0.5, vh * 0.85);
      if (Math.random() < 0.5) { var t = y0; y0 = y1; y1 = t; }
    } else {                      // steep swoop across the middle
      y0 = rand(-vh * 0.05, vh * 0.2);
      y1 = rand(vh * 0.6, vh * 0.95);
    }

    return {
      x0: toLeft ? vw + pad : -pad,
      x1: toLeft ? -pad : vw + pad,
      y0: y0,
      y1: y1,
      toLeft: toLeft,
      // Vertical undulation, so the path reads as flight rather than a slide.
      amp: rand(vh * 0.04, vh * 0.12),
      waves: rand(1.2, 2.4),
      phase: rand(0, Math.PI * 2),
      dur: cfg.crossMs * rand(0.82, 1.25)
    };
  }

  function posAt(r, t) {
    return {
      x: r.x0 + (r.x1 - r.x0) * t,
      y: r.y0 + (r.y1 - r.y0) * t + Math.sin(r.phase + t * Math.PI * r.waves) * r.amp
    };
  }

  function fly() {
    if (flying || document.hidden) { schedule(); return; }
    flying = true;
    if (!sky) build();

    var r = route();
    var start = null;
    // Re-read the wingspan each flight so a rotated phone or resized window
    // gets a correctly-scaled bird without needing a reload.
    bird.style.width = wingspan() + 'px';
    sky.style.display = 'block';
    strip.style.animationPlayState = 'running';

    function step(ts) {
      if (start === null) start = ts;
      var t = (ts - start) / r.dur;
      if (t >= 1) { done(); return; }

      var p = posAt(r, t);
      // Bank the bird along its own tangent by sampling slightly ahead.
      var q = posAt(r, Math.min(t + 0.012, 1));
      var ang = Math.atan2(q.y - p.y, Math.abs(q.x - p.x)) * (180 / Math.PI);
      ang = Math.max(-24, Math.min(24, ang));

      // The artwork faces right, so flip it when travelling left. The flip is
      // applied after the rotation so the bank still reads the right way up.
      var flip = r.toLeft ? ' scaleX(-1)' : '';
      bird.style.transform =
        'translate3d(' + p.x.toFixed(1) + 'px,' + p.y.toFixed(1) + 'px,0) ' +
        'translate(-50%,-50%) ' +
        'rotate(' + (r.toLeft ? -ang : ang).toFixed(2) + 'deg)' + flip;

      requestAnimationFrame(step);
    }

    function done() {
      flying = false;
      sky.style.display = 'none';
      strip.style.animationPlayState = 'paused';
      schedule();
    }

    requestAnimationFrame(step);
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(fly, delay != null ? delay : rand(cfg.minGapMs, cfg.maxGapMs));
  }

  // Don't burn frames or bank up flights while the tab is in the background.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      clearTimeout(timer);
    } else if (!flying) {
      schedule(rand(2000, 6000));
    }
  });

  function init() { schedule(cfg.firstMs); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Lets the theme editor (or a "preview" button) trigger a pass on demand.
  window.MnMMagpie = {
    flyNow: function () { clearTimeout(timer); if (!flying) fly(); },
    config: cfg
  };
})();
